import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { mergedPersonas } from '@platform/schema/content';

import { PERSONAS, personaName } from './personas';

import type { PersonaLabel } from '@platform/schema/content';

/**
 * Who a lesson is for and what it is about — spec 016 §6.
 *
 * Reports the whole set on every change, because the write replaces rather than merges:
 * an empty array is how a reviewer removes the last label the planner guessed, and a
 * component that only reported additions could never express it.
 *
 * The tag field is `freeSolo` on purpose. The vocabulary is open by design (016 §1), and
 * a reviewer who needs a word the planner has not written should not have to wait for a
 * migration to say it.
 */
export function LabelEditor({
  personas,
  tags,
  vocabulary,
  onChange,
}: {
  personas: PersonaLabel[];
  tags: string[];
  vocabulary: string[];
  onChange: (next: { personas: PersonaLabel[]; tags: string[] }) => void;
}) {
  return (
    <Stack spacing={2}>
      <Autocomplete
        multiple
        id="lesson-personas"
        options={[...PERSONAS]}
        value={personas.map(({ persona }) => persona)}
        getOptionLabel={personaName}
        onChange={(_event, next) => {
          onChange({ personas: mergedPersonas(personas, next), tags });
        }}
        renderValue={(value, getProps) =>
          value.map((persona, index) => (
            <Chip
              {...getProps({ index })}
              key={persona}
              size="small"
              label={personaName(persona)}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label="Personas" size="small" />
        )}
      />

      <Autocomplete
        multiple
        freeSolo
        id="lesson-tags"
        options={vocabulary}
        value={tags}
        onChange={(_event, next) => {
          onChange({ personas, tags: next.map((tag) => tag.trim()) });
        }}
        renderValue={(value, getProps) =>
          value.map((tag, index) => (
            <Chip {...getProps({ index })} key={tag} size="small" label={tag} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tags"
            size="small"
            helperText="Reuse one that exists where you can."
          />
        )}
      />
    </Stack>
  );
}
